import React from "react";
import { Link } from "react-router-dom";
import { StarField, Meteors } from "../motion/space-primitives";

const TELEGRAM = "https://t.me/nodefleet";

const COLUMNS = [
  {
    title: "Products",
    links: [
      { label: "Blockchain", href: "https://datanodes.nodefleet.net/" },
      { label: "Networks Reference", to: "/networks" },
      { label: "Storage", href: "https://datahub.nodefleet.net" },
      { label: "AI", href: "https://aiviber.io/" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "Staking", href: "https://fleetwallet.nodefleet.net/" },
      { label: "Consulting", href: "https://nodes.nodefleet.org/" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Portfolio", href: "https://nodefleet.net/portfolio" },
      { label: "Support", href: TELEGRAM },
    ],
  },
];

// External links open in a new tab; in-page anchors (#) stay put.
const isExternal = (href) => href.startsWith("http");

// `bg` / `accent` let a themed page (e.g. a blog post) tint the footer to match
// its section color; both fall back to the brand defaults.
const Footer = ({ bg, accent = "#99DFAF" }) => {
  return (
    <footer
      className="relative w-full overflow-hidden border-t border-white/10 py-16 text-white"
      style={{ backgroundColor: bg || "#12111d", "--footer-accent": accent }}
    >
      {/* Starfield drifting right→left + falling meteors (no ships) */}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div
          className="bg-drift absolute inset-y-0 left-0 flex h-full"
          style={{ width: "200%", animationDuration: "90s" }}
        >
          <div className="relative h-full w-1/2 shrink-0">
            <StarField count={36} />
          </div>
          <div className="relative h-full w-1/2 shrink-0">
            <StarField count={36} />
          </div>
        </div>
      </div>
      <Meteors count={3} className="z-0 opacity-70" />

      {/* Articles banner — promotes the news/engineering-notes section */}
      <div className="relative z-10 mx-auto mb-12 max-w-7xl px-8 max-sm:px-6 lg:px-20">
        <Link
          to="/articles"
          className="group flex flex-col items-start justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-5 no-underline transition-colors hover:border-primary/40 sm:flex-row sm:items-center"
        >
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">
              Articles
            </span>
            <p className="mt-1 text-lg font-semibold text-white">
              Read the latest from Nodefleet
            </p>
          </div>
          <span className="shrink-0 text-sm font-semibold text-primary transition-transform group-hover:translate-x-1">
            Explore articles →
          </span>
        </Link>
      </div>

      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-4 gap-10 px-8 max-sm:grid-cols-2 max-sm:px-6 lg:px-20">
        <div className="max-sm:col-span-2">
          <img
            src="/assets/icons/Logo_Nodefleet.svg"
            alt="Nodefleet"
            className="h-7 w-auto"
          />
          <p className="mt-4 text-sm text-gray-400">
            The future cloud infrastructure
          </p>
        </div>

        {COLUMNS.map((col) => (
          <div key={col.title}>
            <h4 className="font-semibold text-white">{col.title}</h4>
            <ul className="mt-4 space-y-2">
              {col.links.map((link) => (
                <li key={link.label}>
                  {link.to ? (
                    <Link
                      to={link.to}
                      className="text-sm text-gray-400 no-underline transition-colors hover:[color:var(--footer-accent)]"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      {...(isExternal(link.href)
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                      className="text-sm text-gray-400 transition-colors hover:[color:var(--footer-accent)]"
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </footer>
  );
};

export default Footer;
